import BaseAPIService from './baseAPI';

// Admin Types
export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  created_at: string;
  updated_at?: string;
  last_login?: string;
  roles: string[];
  subscription?: {
    plan: string;
    status: string;
    expires_at?: string;
  };
  permissions: string[];
  daily_posts_used?: number;
  daily_posts_limit?: number;
}

export interface UserRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  created_at: string;
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  daily_post_limit: number;
  features: Record<string, boolean>;
  is_active: boolean;
}

export interface SystemStats {
  total_users: number;
  active_users: number;
  total_posts: number;
  posts_today: number;
  total_ai_generations: number;
  ai_generations_today: number;
  subscription_breakdown: Record<string, number>;
  recent_registrations: Array<{
    id: string;
    email: string;
    created_at: string;
  }>;
}

export interface UserCreateRequest {
  email: string;
  password: string;
  full_name?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  roles?: string[];
}

export interface UserUpdateRequest {
  email?: string;
  full_name?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  roles?: string[];
}

export interface UserRoleAssignRequest {
  role: string;
  action: 'assign' | 'remove';
}

class AdminAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  // User Management
  async getAllUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: 'active' | 'inactive' | 'all';
    sort_by?: string;
    order?: 'asc' | 'desc';
  } = {}): Promise<{
    data: {
      users: AdminUser[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
      };
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.status) queryParams.append('status', params.status);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.order) queryParams.append('order', params.order);

    return this.get<any>(`/admin/users?${queryParams.toString()}`);
  }

  async getUser(userId: string): Promise<{ data: AdminUser }> {
    return this.get<{ data: AdminUser }>(`/admin/users/${userId}`);
  }

  async createUser(userData: UserCreateRequest): Promise<{ data: AdminUser }> {
    return this.post<{ data: AdminUser }>('/admin/users', userData);
  }

  async updateUser(userId: string, userData: UserUpdateRequest): Promise<{ data: AdminUser }> {
    return this.put<{ data: AdminUser }>(`/admin/users/${userId}`, userData);
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/admin/users/${userId}`);
  }

  async deactivateUser(userId: string): Promise<{ data: AdminUser }> {
    return this.post<{ data: AdminUser }>(`/admin/users/${userId}/deactivate`);
  }

  async activateUser(userId: string): Promise<{ data: AdminUser }> {
    return this.post<{ data: AdminUser }>(`/admin/users/${userId}/activate`);
  }

  // Role Management
  async assignUserRole(userId: string, roleData: UserRoleAssignRequest): Promise<{ data: AdminUser }> {
    return this.post<{ data: AdminUser }>(`/admin/users/${userId}/roles`, roleData);
  }

  async getUserRoles(userId: string): Promise<{ data: UserRole[] }> {
    return this.get<{ data: UserRole[] }>(`/admin/users/${userId}/roles`);
  }

  // Subscription Management
  async updateUserSubscription(userId: string, subscriptionData: {
    plan_id: string;
    expires_at?: string;
  }): Promise<{ data: AdminUser }> {
    return this.post<{ data: AdminUser }>(`/admin/users/${userId}/subscription`, subscriptionData);
  }

  async getUserUsage(userId: string): Promise<{
    data: {
      daily_posts_used: number;
      daily_posts_limit: number;
      monthly_posts_used: number;
      ai_generations_used: number;
      image_searches_used: number;
    };
  }> {
    return this.get<any>(`/admin/users/${userId}/usage`);
  }

  // System Management
  async getSystemStats(): Promise<{ data: SystemStats }> {
    return this.get<{ data: SystemStats }>('/admin/stats');
  }

  async getSystemHealth(): Promise<{
    data: {
      status: 'healthy' | 'warning' | 'error';
      services: {
        database: string;
        redis: string;
        openai: string;
        unsplash: string;
      };
      uptime: number;
      memory_usage: number;
      cpu_usage: number;
    };
  }> {
    return this.get<any>('/admin/health');
  }

  // Role & Permission Management
  async getAllRoles(): Promise<{ data: UserRole[] }> {
    return this.get<{ data: UserRole[] }>('/admin/roles');
  }

  async createRole(roleData: {
    name: string;
    description: string;
    permissions: string[];
  }): Promise<{ data: UserRole }> {
    return this.post<{ data: UserRole }>('/admin/roles', roleData);
  }

  async updateRole(roleId: string, roleData: {
    name?: string;
    description?: string;
    permissions?: string[];
  }): Promise<{ data: UserRole }> {
    return this.put<{ data: UserRole }>(`/admin/roles/${roleId}`, roleData);
  }

  async deleteRole(roleId: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/admin/roles/${roleId}`);
  }

  async getAllPermissions(): Promise<{ data: Permission[] }> {
    return this.get<{ data: Permission[] }>('/admin/permissions');
  }

  // Subscription Plan Management
  async getAllPlans(): Promise<{ data: SubscriptionPlan[] }> {
    return this.get<{ data: SubscriptionPlan[] }>('/admin/plans');
  }

  async createPlan(planData: {
    name: string;
    price: number;
    currency: string;
    interval: string;
    daily_post_limit: number;
    features: Record<string, boolean>;
  }): Promise<{ data: SubscriptionPlan }> {
    return this.post<{ data: SubscriptionPlan }>('/admin/plans', planData);
  }

  async updatePlan(planId: string, planData: {
    name?: string;
    price?: number;
    daily_post_limit?: number;
    features?: Record<string, boolean>;
    is_active?: boolean;
  }): Promise<{ data: SubscriptionPlan }> {
    return this.put<{ data: SubscriptionPlan }>(`/admin/plans/${planId}`, planData);
  }

  async deletePlan(planId: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/admin/plans/${planId}`);
  }

  async getPlanDetails(planId: string): Promise<{ data: SubscriptionPlan }> {
    return this.get<{ data: SubscriptionPlan }>(`/admin/plans/${planId}`);
  }

  // Activity Logs
  async getActivityLogs(params: {
    user_id?: string;
    action?: string;
    resource?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    data: Array<{
      id: string;
      user_id: string;
      user_email: string;
      action: string;
      resource: string;
      details: Record<string, any>;
      ip_address: string;
      user_agent: string;
      created_at: string;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.action) queryParams.append('action', params.action);
    if (params.resource) queryParams.append('resource', params.resource);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return this.get<any>(`/admin/logs?${queryParams.toString()}`);
  }

  // Bulk Operations
  async bulkUpdateUsers(operation: {
    user_ids: string[];
    action: 'activate' | 'deactivate' | 'delete' | 'assign_role' | 'remove_role';
    data?: Record<string, any>;
  }): Promise<{
    data: {
      success_count: number;
      failed_count: number;
      failures: Array<{
        user_id: string;
        error: string;
      }>;
    };
  }> {
    return this.post<any>('/admin/users/bulk', operation);
  }

  // Export Users
  async exportUsers(params: {
    format: 'csv' | 'excel';
    filters?: {
      role?: string;
      status?: string;
      date_from?: string;
      date_to?: string;
    };
  }): Promise<{
    data: {
      download_url: string;
      filename: string;
    };
  }> {
    return this.post<any>('/admin/users/export', params);
  }

  // Dashboard Analytics for Admin
  async getAdminDashboard(): Promise<{
    data: {
      user_growth: Array<{
        date: string;
        new_users: number;
        total_users: number;
      }>;
      subscription_metrics: {
        conversion_rate: number;
        churn_rate: number;
        revenue: number;
      };
      platform_usage: Array<{
        platform: string;
        posts: number;
        users: number;
      }>;
      system_alerts: Array<{
        type: string;
        message: string;
        severity: 'low' | 'medium' | 'high';
        created_at: string;
      }>;
    };
  }> {
    return this.get<any>('/admin/dashboard');
  }
}

export const adminAPI = new AdminAPIService();
export default adminAPI; 